import "react-day-picker/lib/style.css";

import dayjs from "dayjs";
import { useState } from "react";
import DayPicker, { DateUtils, RangeModifier } from "react-day-picker";
import styled from "styled-components";

import { FormGroup, Label, TextField } from "@runwayhealth/runway-components-react";

const RangePickerElement = styled(DayPicker)`
  .Range
    .DayPicker-Day--selected:not(.DayPicker-Day--start):not(.DayPicker-Day--end):not(
      .DayPicker-Day--outside
    ) {
    background-color: #f0f8ff !important;
    color: #4a90e2;
  }

  .DayPicker-Day {
    border-radius: 0 !important;
  }

  .DayPicker-Day--today {
    color: #4272da;
  }

  // Hover on class
  .DayPicker-Day--selected:not(.DayPicker-Day--disabled):not(.DayPicker-Day--outside) {
    background-color: #96b6fa;
    border-radius: 0px;
    color: white;
  }
  .DayPicker-Day--selected:not(.DayPicker-Day--disabled):not(.DayPicker-Day--outside):hover {
    background: white;
    border-radius: 0px;
    color: #96b6fa;
  }
`;

const HeaderReference = styled(FormGroup)`
  padding-left: 15%;
  padding-right: 15%;
  text-align: left;
  input[type="text"] {
    text-align: center;
  }
  input[type="text"] {
    text-align: center;
  }
  input:disabled {
    color: #000;
    background-color: #f2f2f2;
    font-weight: 500;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    display: inline-block;
  }
`;

export interface DateRangePickerValue extends RangeModifier {
  days: number;
}

type DateRangePickerProps = {
  min: Date;
  onChange: (value: DateRangePickerValue) => void;
  fromTitle?: string;
  toTitle?: string;
};

const DateRangePicker = ({ min, onChange, fromTitle, toTitle }: DateRangePickerProps) => {
  const ModifierInitialState = {
    from: null,
    to: null,
  };

  const [rangeModifier, setRangeModifier] = useState<RangeModifier>(ModifierInitialState);
  const [enteredTo, setEnteredTo] = useState<Date | null>(null);
  const modifiers = { start: rangeModifier.from, end: enteredTo };
  const disableBeforeMin = dayjs(min).add(1, "day").toDate();

  const disabledDays = {
    before: rangeModifier.from ?? disableBeforeMin,
    after: rangeModifier.to,
  };

  const handleDayClick = (day: Date) => {
    if (day < min) {
      return;
    }

    const range = DateUtils.addDayToRange(day, rangeModifier);
    setRangeModifier(range);

    const { from, to } = rangeModifier;

    if (from && to && day >= from && day <= to) {
      handleResetClick();
      return;
    }
    if (isSelectingFirstDay(from, to, day)) {
      setRangeModifier({
        from: day,
        to: null,
      });
      setEnteredTo(null);
    } else {
      setRangeModifier((prev) => ({ ...prev, to: day }));
      setEnteredTo(day);
    }

    const newRange = {
      from: rangeModifier.from,
      to: day,
    };

    if (newRange.from && newRange.to && newRange.from < newRange.to) {
      onChange({ ...newRange, days: getDays(newRange) });
    }
  };

  const isSelectingFirstDay = (
    from: Date | null | undefined,
    to: Date | null | undefined,
    day: Date
  ) => {
    const isBeforeFirstDay = from && DateUtils.isDayBefore(day, from);
    const isRangeSelected = from && to;
    return !from || isBeforeFirstDay || isRangeSelected;
  };

  const handleDayMouseEnter = (day: Date): void => {
    if (
      !isSelectingFirstDay(
        rangeModifier?.from ?? ModifierInitialState.from,
        rangeModifier?.to ?? ModifierInitialState.to,
        day
      )
    ) {
      setEnteredTo(day);
    }
  };

  const handleResetClick = () => {
    setRangeModifier(ModifierInitialState);
    onChange({ ...ModifierInitialState, days: 0 });
  };

  const getDays = (range: RangeModifier) => {
    // Pass Date objects directly to dayjs to avoid locale-dependent parsing issues
    const startDate = dayjs(range.from);
    const endDate = dayjs(range.to);

    return endDate.diff(startDate, "day");
  };

  return (
    <>
      <HeaderReference>
        <TextField
          id="calendar-ref-header"
          value={
            rangeModifier.from || rangeModifier.to
              ? `${rangeModifier.from?.toLocaleDateString("en-us", {
                  month: "short",
                  day: "numeric",
                })} - ${
                  rangeModifier.to?.toLocaleDateString("en-us", {
                    month: "short",
                    day: "numeric",
                  }) ?? toTitle
                }`
              : ""
          }
          placeholder={`${fromTitle ?? "From"} - ${toTitle ?? "To"}`}
          disabled
        />
      </HeaderReference>
      <hr></hr>
      <RangePickerElement
        data-testid="date-picker"
        className="Range"
        numberOfMonths={2}
        selectedDays={[
          rangeModifier?.from ?? new Date(),
          { from: rangeModifier?.from, to: enteredTo },
        ]}
        modifiers={{
          start: modifiers.start ?? new Date(),
          end: modifiers.end ?? new Date(),
        }}
        onDayClick={handleDayClick}
        onDayMouseEnter={handleDayMouseEnter}
        disabledDays={disabledDays}
      />
    </>
  );
};

export default DateRangePicker;
